/**
 * Keyboard moves for a two-level navigation tree (WAI-ARIA tree view pattern):
 * ↑/↓ move between visible items, Home/End jump to the ends, → opens a closed
 * parent or steps into an open one, ← closes an open parent or steps out of a
 * child. Enter or Space on a parent opens or closes it; on a child, Enter is
 * left to the item itself (each child is a link). A pinned parent (the token
 * being viewed) stays open: what would close it only nudges it.
 */
export interface TreeItem {
  level: 1 | 2;
  /** a level-1 item that has children */
  expandable: boolean;
  expanded: boolean;
  /** an open parent that cannot be closed */
  pinned?: boolean;
}

export interface TreeMove {
  /** index of the item to focus */
  focus?: number;
  /** index of the level-1 item to open or close */
  toggle?: number;
  /** index of a pinned item asked to close: it stays open, and only says so */
  nudge?: number;
}

export function treeKey(items: readonly TreeItem[], current: number, key: string): TreeMove | undefined {
  if (items.length === 0) return undefined;
  const at = items[current];
  if (!at) return { focus: 0 };
  switch (key) {
    case "ArrowDown":
      return current < items.length - 1 ? { focus: current + 1 } : undefined;
    case "ArrowUp":
      return current > 0 ? { focus: current - 1 } : undefined;
    case "Home":
      return { focus: 0 };
    case "End":
      return { focus: items.length - 1 };
    case "ArrowRight":
      if (at.level !== 1 || !at.expandable) return undefined;
      if (!at.expanded) return { toggle: current };
      return items[current + 1]?.level === 2 ? { focus: current + 1 } : undefined;
    case "ArrowLeft": {
      if (at.level === 1)
        return at.pinned ? { nudge: current } : at.expanded ? { toggle: current } : undefined;
      for (let i = current - 1; i >= 0; i--) if (items[i]!.level === 1) return { focus: i };
      return undefined;
    }
    case "Enter":
    case " ":
      if (at.level !== 1 || !at.expandable) return undefined;
      return at.pinned ? { nudge: current } : { toggle: current };
    default:
      return undefined;
  }
}

/** What the rail's token tree shows: which tokens are open, and the item Tab lands on. */
export interface TokenTreeState {
  open: Record<string, boolean>;
  /** every visible item: a token's job id, `<job id>/<section id>`, or `<job id>/@default` */
  keys: string[];
  /** the page being viewed, if it is in the tree (else its token) */
  here: string | undefined;
}

/** The tree key of a token's saved default view, listed before its sections. */
export const defaultViewKey = (jobId: string) => `${jobId}/@default`;

/**
 * The token being viewed (`activeId`) is always open; any other token is open
 * when `opened` (the tokens the reader opened by hand) says so. Leaving a
 * token therefore folds it again, unless the reader had opened it.
 * `withDefault` holds the tokens whose saved default view is not their
 * overview: the tree lists that view as an extra first page.
 */
export function tokenTree(
  jobs: readonly { id: string; chain: string; token: string }[],
  sections: readonly { id: string; path: (chain: string, token: string) => string }[],
  activeId: string | undefined,
  opened: Record<string, boolean>,
  pathname: string,
  withDefault: ReadonlySet<string> = new Set(),
): TokenTreeState {
  const open = Object.fromEntries(jobs.map((j) => [j.id, j.id === activeId || opened[j.id] === true]));
  const keys = jobs.flatMap((j) => [
    j.id,
    ...(open[j.id]
      ? [...(withDefault.has(j.id) ? [defaultViewKey(j.id)] : []), ...sections.map((s) => `${j.id}/${s.id}`)]
      : []),
  ]);
  const active = jobs.find((j) => j.id === activeId);
  const path = pathname.replace(/\/$/, "");
  const section = active && sections.find((s) => s.path(active.chain, active.token) === path);
  return { open, keys, here: active && (section ? `${active.id}/${section.id}` : active.id) };
}
